import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { getLeases } from '../services/api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Plus, FileText, Calendar } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import AddLeaseModal from '../components/modals/AddLeaseModal';

export default function Leases() {
  const [leases, setLeases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);

  useEffect(() => {
    loadLeases();
  }, []);

  const loadLeases = async () => {
    try {
      const data = await getLeases();
      setLeases(data);
    } catch (error) {
      toast.error('Failed to load leases');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      <div data-testid="leases-page" className="max-w-7xl mx-auto animate-fade-in">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-2">Leases</h1>
            <p className="text-gray-600">Manage rental agreements</p>
          </div>
          <Button data-testid="add-lease-btn" className="gap-2" onClick={() => setShowAddModal(true)}>
            <Plus className="w-4 h-4" />
            New Lease
          </Button>
        </div>

        {loading ? (
          <div className="flex justify-center py-20">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
          </div>
        ) : leases.length === 0 ? (
          <Card data-testid="empty-state">
            <CardContent className="flex flex-col items-center justify-center py-20">
              <FileText className="w-16 h-16 text-gray-300 mb-4" />
              <p className="text-gray-500 text-lg mb-4">No leases yet</p>
              <Button data-testid="add-first-lease-btn" className="gap-2" onClick={() => setShowAddModal(true)}>
                <Plus className="w-4 h-4" />
                Create First Lease
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {leases.map((lease) => (
              <Card key={lease.id} data-testid={`lease-card-${lease.id}`} className="card-hover">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <FileText className="w-5 h-5 text-blue-600" />
                        <span className="font-semibold text-gray-900">Lease #{lease.id.slice(0, 8)}</span>
                        <span className={`badge ${lease.status === 'active' ? 'badge-success' : lease.status === 'expired' ? 'badge-danger' : 'badge-warning'}`}>
                          {lease.status}
                        </span>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-sm">
                        <div>
                          <span className="text-gray-600">Start Date:</span>
                          <div className="font-medium text-gray-900 flex items-center gap-1 mt-1">
                            <Calendar className="w-4 h-4" />
                            {format(new Date(lease.start_date), 'MMM dd, yyyy')}
                          </div>
                        </div>
                        <div>
                          <span className="text-gray-600">End Date:</span>
                          <div className="font-medium text-gray-900 flex items-center gap-1 mt-1">
                            <Calendar className="w-4 h-4" />
                            {format(new Date(lease.end_date), 'MMM dd, yyyy')}
                          </div>
                        </div>
                        <div>
                          <span className="text-gray-600">Rent:</span>
                          <div className="font-medium text-blue-600 mt-1">
                            ${lease.rent_amount} / {lease.rent_frequency}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        <AddLeaseModal
          open={showAddModal}
          onClose={() => setShowAddModal(false)}
          onSuccess={loadLeases}
        />
      </div>
    </Layout>
  );
}